import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Badge } from "@/components/ui/badge";
import {
  PenTool,
  Code,
  Briefcase,
  Camera,
  Type,
  Megaphone,
} from "lucide-react";
import ProfileActionsBar from "./ProfileActionsBar";
import ProfileStatsSection from "./ProfileStatsSection";
import { ProfileData } from "@/hooks/useProfiles";
import SocialShare from "./SocialShare";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQueryClient } from "@tanstack/react-query";
import { VerifiedBadge } from "../designer-card/CardContent";
import { getCountryFlagEmoji } from "@/utils/countryFlags";
import { convertSupabaseUrlToRenderUrl } from "@/utils/imageUtils";

interface ProfileHeaderProps {
  profile: ProfileData;
  placeholderImage: string;
}

// Type for React Query profile data
interface ProfileQueryData {
  data: ProfileData;
  [key: string]: unknown;
}

const ProfileHeader = ({
  profile,
  placeholderImage,
}: ProfileHeaderProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  // Use refs to track like state changes and prevent loops
  const lastLikeStateRef = useRef<{ isLiked: boolean; count: number }>({
    isLiked: profile.is_liked || false,
    count: profile.like_count || 0,
  });

  // Track if we're currently updating to prevent circular updates
  const isUpdatingRef = useRef(false);

  // Memoize profile data to prevent unnecessary re-renders
  const stableProfile = useMemo(
    () => ({
      ...profile,
      is_liked: profile.is_liked || false,
      like_count: profile.like_count || 0,
    }),
    [
      profile.id,
      profile.username,
      profile.display_name,
      profile.avatar_url,
      profile.profession,
      profile.location,
      profile.skills,
      profile.is_liked,
      profile.like_count,
      profile.is_featured,
    ]
  );

  const getInitials = useCallback((name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }, []);

  const handleLikeChange = useCallback(
    (isLiked: boolean, count?: number) => {
      // Prevent circular updates
      if (isUpdatingRef.current) {
        return;
      }

      // Check if this is actually a change
      const currentState = lastLikeStateRef.current;
      if (currentState.isLiked === isLiked) {
        return;
      }

      isUpdatingRef.current = true;

      // Use provided count or calculate new like count
      const newLikeCount =
        count !== undefined
          ? count
          : Math.max(0, currentState.count + (isLiked ? 1 : -1));

      // Update our tracking refs
      lastLikeStateRef.current = {
        isLiked,
        count: newLikeCount,
      };

      // Update React Query cache without triggering re-renders
      if (profile.id) {
        queryClient.setQueryData(
          ["profile", profile.id],
          (oldData: unknown): ProfileQueryData | unknown => {
            if (!oldData || typeof oldData !== "object") return oldData;

            const profileData = oldData as ProfileQueryData;

            return {
              ...profileData,
              data: {
                ...profileData.data,
                like_count: newLikeCount,
                is_liked: isLiked,
              },
            };
          }
        );
      }

      // Reset the updating flag after a brief delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    },
    [profile.id, queryClient]
  );

  // Update refs when profile props change (without causing re-renders)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      lastLikeStateRef.current = {
        isLiked: profile.is_liked || false,
        count: profile.like_count || 0,
      };
    }
  }, [profile.is_liked, profile.like_count]);

  const formatLocation = useCallback((location: string | null): string => {
    if (!location) return "";
    
    const specialCases: Record<string, string> = {
      'united-kingdom': 'United Kingdom',
      'united kingdom': 'United Kingdom',
      'uk': 'United Kingdom',
      'united-states': 'United States',
      'united states': 'United States',
      'usa': 'United States',
      'us': 'United States',
      'south-africa': 'South Africa',
      'south africa': 'South Africa',
      'ivory-coast': 'Ivory Coast',
      'ivory coast': 'Ivory Coast',
      'cape-verde': 'Cape Verde',
      'cape verde': 'Cape Verde'
    };
    
    const lowerLocation = location.toLowerCase().trim();
    if (specialCases[lowerLocation]) {
      return specialCases[lowerLocation];
    }
    
    return location
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const getProfessionIcon = useCallback((profession: string) => {
    const lowerProf = profession.toLowerCase();
    if (
      lowerProf.includes("design") ||
      lowerProf.includes("graphic") ||
      lowerProf.includes("ui") ||
      lowerProf.includes("ux")
    ) {
      return <PenTool className="h-4 w-4 mr-1 shrink-0" />;
    } else if (
      lowerProf.includes("develop") ||
      lowerProf.includes("engineer") ||
      lowerProf.includes("code") ||
      lowerProf.includes("program")
    ) {
      return <Code className="h-4 w-4 mr-1 shrink-0" />;
    } else if (lowerProf.includes("photo") || lowerProf.includes("camera")) {
      return <Camera className="h-4 w-4 mr-1 shrink-0" />;
    } else if (
      lowerProf.includes("write") ||
      lowerProf.includes("content") ||
      lowerProf.includes("editor")
    ) {
      return <Type className="h-4 w-4 mr-1 shrink-0" />;
    } else if (
      lowerProf.includes("market") ||
      lowerProf.includes("brand") ||
      lowerProf.includes("social")
    ) {
      return <Megaphone className="h-4 w-4 mr-1 shrink-0" />;
    } else {
      return <Briefcase className="h-4 w-4 mr-1 shrink-0" />;
    }
  }, []);

  const getProfessionFromSkills = useCallback((): string => {
    if (stableProfile.profession) return stableProfile.profession;
    if (stableProfile.skills && stableProfile.skills.length > 0) {
      const skill = stableProfile.skills[0];
      return skill
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return "Designer";
  }, [stableProfile.profession, stableProfile.skills]);

  // Memoize computed values
  const displayName = useMemo(() => {
    return (
      stableProfile.display_name ||
      stableProfile.username ||
      "Anonymous Designer"
    );
  }, [stableProfile.display_name, stableProfile.username]);

  const profession = useMemo(() => {
    return getProfessionFromSkills();
  }, [getProfessionFromSkills]);

  return (
    <div className="relative mb-8">
      <div className="mx-auto relative z-10 sm:px-6 lg:px-0 px-0">
        <div className="w-full bg-gradient-to-b from-white via-gray-50 to-gray-100/80 backdrop-blur-sm rounded-lg shadow-sm p-6 sm:p-8 bg-amber-200">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0 md:block mx-auto md:mx-0">
              <div className="relative w-28 h-28 overflow-hidden rounded-lg border-4 border-white shadow-sm">
                {!imageLoaded && (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg">
                    <span className="text-2xl font-bold text-gray-400">
                      {getInitials(displayName)}
                    </span>
                  </div>
                )}
                <img
                  src={
                    convertSupabaseUrlToRenderUrl(stableProfile.avatar_url) ||
                    placeholderImage
                  }
                  alt={displayName}
                  className={`h-full w-full object-cover transition-opacity duration-300 ${
                    imageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setImageLoaded(true)}
                />
              </div>
            </div>

            <div className="flex-1 w-full">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className={isMobile ? "text-center w-full" : ""}>
                  <h1
                    className={`font-bold text-base flex items-center gap-1 flex-nowrap ${
                      isMobile
                        ? "flex justify-center"
                        : "flex items-center gap-2"
                    }`}
                    style={{ minWidth: 0 }}
                  >
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap block max-w-[220px] md:max-w-[320px]">
                      {displayName}
                    </span>
                    {stableProfile.is_featured && (
                      <span className="whitespace-nowrap flex-shrink-0">
                        <VerifiedBadge width={16} height={16} />
                      </span>
                    )}
                  </h1>

                  <div
                    className={`mt-2 ${
                      isMobile
                        ? "flex items-center justify-center flex-wrap gap-2"
                        : "flex items-center"
                    }`}
                  >
                    <span className="flex items-center text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                      {getProfessionIcon(stableProfile.profession || "")}
                      <span className="truncate">
                        {stableProfile.profession || "Designer"}
                      </span>
                    </span>
                    {stableProfile.location && (
                      <span
                        className={`flex items-center text-sm text-muted-foreground whitespace-nowrap ${
                          isMobile ? "" : "ml-4"
                        }`}
                      >
                        <span className="mr-1.5 text-base">
                          {getCountryFlagEmoji(stableProfile.location) || "🌍"}
                        </span>
                        {formatLocation(stableProfile.location)}
                      </span>
                    )}
                  </div>

                  <div
                    className={`mt-3 ${isMobile ? "flex justify-center" : ""}`}
                  >
                    <ProfileStatsSection
                      profile={stableProfile}
                      likeCount={stableProfile.like_count}
                    />
                  </div>
                </div>

                <div
                  className={`flex ${
                    isMobile ? "flex-row" : "items-center"
                  } justify-center md:justify-end w-full gap-2 md:min-w-[280px]`}
                >
                  {/* Swapped the order of the buttons for desktop view only */}
                  {!isMobile ? (
                    <>
                      <div className="h-10">
                        <SocialShare
                          title={`${displayName}'s Profile`}
                          description={`See ${displayName}'s portfolio: a ${profession} from ${
                            stableProfile.location
                              ? formatLocation(stableProfile.location)
                              : "Africa"
                          }. Explore past projects and connect to hire or collaborate.`}
                          url={`https://world-class.design${profile.username_slug ? `/u/${encodeURIComponent(profile.username_slug)}` : `/profile/${profile.id}`}`}
                        />
                      </div>

                      <div className="h-10">
                        <ProfileActionsBar
                          profile={stableProfile}
                          isLiked={stableProfile.is_liked}
                          onLikeChange={handleLikeChange}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-10">
                        <ProfileActionsBar
                          profile={stableProfile}
                          isLiked={stableProfile.is_liked}
                          onLikeChange={handleLikeChange}
                        />
                      </div>

                      <div className="h-10 mb-2">
                        <SocialShare
                          title={`${displayName}'s Profile`}
                          description={`See ${displayName}'s portfolio: a ${profession} from ${
                            stableProfile.location
                              ? formatLocation(stableProfile.location)
                              : "Africa"
                          }. Explore past projects and connect to hire or collaborate.`}
                          url={`https://world-class.design${profile.username_slug ? `/u/${encodeURIComponent(profile.username_slug)}` : `/profile/${profile.id}`}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
